/**
 * @file App.tsx
 * @path apps/website/src/App.tsx
 * @description Centralized UI Orchestrator.
 * Contains routing configuration and handles the global SplashScreen.
 */

import { useState } from "react";
import { createBrowserRouter, RouterProvider } from "react-router-dom";

// UI Components
import { MainLayout, SplashScreen } from "@/components";
import {
  BookingConfirmationPage,
  BookingFormPage,
  ContactUsPage,
  ExperienceDetailsPage,
  ExperiencesPage,
  HomePage,
  JoinUsPage,
  LicensePage,
  PrivacyPolicyPage,
  TermsOfServicePage,
} from "@/pages";
import { PATHS } from "./config";

/**
 * Router Configuration
 * Defined outside the component to prevent re-creation on every render.
 */
const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <MainLayout />,
      children: [
        { index: true, element: <HomePage /> },
        { path: PATHS.EXPERIENCES, element: <ExperiencesPage /> },
        { path: PATHS.JOIN_US, element: <JoinUsPage /> },
        { path: PATHS.CONTACT, element: <ContactUsPage /> },
      ],
    },
    { path: PATHS.EXPERIENCE_DETAILS, element: <ExperienceDetailsPage /> },
    {
      path: PATHS.BOOKING,
      element: <BookingFormPage />,
    },
    {
      path: PATHS.CONFIRMATION,
      element: <BookingConfirmationPage />,
    },
    {
      path: PATHS.LEGAL.LICENSE,
      element: <LicensePage />,
    },
    {
      path: PATHS.LEGAL.PRIVACY,
      element: <PrivacyPolicyPage />,
    },
    {
      path: PATHS.LEGAL.TERMS,
      element: <TermsOfServicePage />,
    },
  ],
  {
    future: {
      v7_relativeSplatPath: true,
    },
  },
);

const App = () => {
  const [showSplash, setShowSplash] = useState(true);

  return (
    <>
      {/* The Splash Screen covers the app until 'onComplete' is triggered.
          We keep the RouterProvider rendered underneath so that it can 
          begin pre-loading assets or data while the user sees the splash.
      */}
      {showSplash && <SplashScreen onComplete={() => setShowSplash(false)} />}

      <RouterProvider router={router} future={{ v7_startTransition: true }} />
    </>
  );
};

export default App;
